import { CopyOutlined, DeleteOutlined, RocketOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  message,
  Row,
  Select,
} from 'antd'
import copy from 'copy-to-clipboard'
import { RentalInfoPeriod } from 'Modules'
import { useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import DatePicker from 'component/date-picker/date-picker'
import { RENTAL_FREQUENCY_OPTIONS, RENTAL_TOKEN_OPTIONS, TOKEN_ADDRESS_MAP } from 'constants/pto'
import { SettingEnum } from 'constants/setting'
import { usePTOV2Context } from 'context/pto-v2-context-provider'
import { parseRewardsPerNFT } from 'extends/pto.extend'
import { useDeployRentalRevenueSharingContract } from 'hooks/pto/use-deploy-rental-revenue-sharing-contract'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { convertNumberToDateTime } from 'utils/datetime'
import { logError } from 'utils/logger'

interface RentalPeriodCardProps {
  name: number
  restField: { fieldKey?: number }
  form: FormInstance
  edit: boolean
  isEditMode: boolean
  locked: boolean
  hasContract: boolean
  period: RentalInfoPeriod | undefined
  firstTokenSymbol: string | null
  onRemove: () => void
}

const RentalPeriodCard = ({
  name,
  restField,
  form,
  edit,
  isEditMode,
  locked,
  hasContract,
  period,
  firstTokenSymbol,
  onRemove,
}: RentalPeriodCardProps) => {
  const { getSetting } = useAppSetting()
  const { address } = useAccount()
  const { ptoDetail, getPTODetail } = usePTOV2Context()
  const { deployRentalPool } = useDeployRentalRevenueSharingContract()
  const [deploying, setDeploying] = useState(false)
  const stakeNFTAddress = getSetting(SettingEnum.STAKE_NFT_ADDRESS)
  const stakeNFTMeyAddress = getSetting(SettingEnum.STAKE_NFT_ADDRESS_MEY)

  const stakingNFTOptions = [
    { label: 'Stake Pool USDC', value: stakeNFTAddress },
    { label: 'Stake Pool MEY', value: stakeNFTMeyAddress },
  ].filter((option) => option.value)

  useEffect(() => {
    const current = form.getFieldValue(['rentalPeriods', name, 'nftStakePool'])

    if (!current) {
      if (stakeNFTAddress) {
        form.setFieldValue(['rentalPeriods', name, 'nftStakePool'], stakeNFTAddress)
      }
      return
    }

    // Normalize saved address to the option value so the Select shows its label
    const matched = [stakeNFTAddress, stakeNFTMeyAddress].find(
      (addr) => addr && String(addr).toLowerCase() === String(current).toLowerCase()
    )
    if (matched && matched !== current) {
      form.setFieldValue(['rentalPeriods', name, 'nftStakePool'], matched)
    }
  }, [stakeNFTAddress, stakeNFTMeyAddress, period?.nftStakePool, form, name])

  const validateAndGetPeriod = async () => {
    const rentalFields = [
      'ownerAddress',
      'startDate',
      'endDate',
      'frequencyDate',
      'rewardsPerNFT',
      'tokenSymbol',
      'tokenReward',
      'annualNetCashflowToInvestor',
      'nftStakePool',
    ].map((field) => ['rentalPeriods', name, field])
    await form.validateFields(rentalFields)
    return form.getFieldValue(['rentalPeriods', name]) as RentalInfoPeriod
  }

  /**
   * V2 (RevenueSharingRentalFactoryV2): one call saves the period (retry-safe -
   * an unconfirmed latest period is reused, not duplicated) and returns the
   * createRevenueRentalPool calldata for the admin wallet to sign.
   */
  const handleSaveAndDeploy = async () => {
    try {
      if (!address) {
        message.error('Please connect wallet first')
        return
      }
      if (!ptoDetail?.contractAddress) {
        message.error('PTO contract not deployed')
        return
      }

      setDeploying(true)
      const formPeriod = await validateAndGetPeriod()

      const { _id, contractAddress: _ca, ...savePayload } = formPeriod

      // 1. BE saves the period and prepares the deploy calldata.
      // rewardsPerNFT goes up in token base units (USDC x10^6, MEY x10^18) -
      // as a string so 18-decimal values keep full precision
      const res = await AppAdmin.createRentalInfoV2(ptoDetail._id, {
        ...savePayload,
        rewardsPerNFT: parseRewardsPerNFT(savePayload.rewardsPerNFT, savePayload.tokenSymbol),
        contractAddress: '',
      } as unknown as RentalInfoPeriod)
      const data = res?.data?.data
      const rentalList = data?.revenueSharingRental
      const deployCall = data?.onChainCreateRentalPoolCall
      const rentalId = deployCall?.params?.rentalId || rentalList?.[rentalList.length - 1]?._id

      if (!rentalId) {
        message.error('Failed to save rental period')
        return
      }
      form.setFieldValue(['rentalPeriods', name, '_id'], rentalId)

      if (!deployCall) {
        message.error('Server did not return the on-chain call')
        getPTODetail()
        return
      }

      // 2. Admin wallet signs the prepared tx
      const poolAddress = await deployRentalPool(address, deployCall)
      if (poolAddress) {
        message.success('Rental contract deployed successfully!')
      }
      getPTODetail()
    } catch (error: any) {
      logError('Deploy rental contract failed', error)
      const errorMessage =
        error?.response?.data?.message || (error instanceof Error ? error.message : 'Unknown error')
      message.error(`Deploy rental contract failed: ${errorMessage}`)
    } finally {
      setDeploying(false)
    }
  }

  const handleDelete = async () => {
    try {
      onRemove()

      if (!period?._id) return

      await AppAdmin.deleteRentalInfo(ptoDetail!._id, period._id)
      message.success('Rental period deleted successfully!')
      getPTODetail()
    } catch (error) {
      logError('Delete rental period failed', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      message.error(`Delete rental period failed: ${errorMessage}`)
    }
  }

  return (
    <Card
      style={{
        borderRadius: 8,
        marginBottom: 16,
        position: 'relative',
        border: '1px solid rgba(59, 63, 69, 0.2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <strong>Rental Information Period {name + 1}</strong>
        <div style={{ display: 'flex', gap: 8 }}>
          {isEditMode && !hasContract && !edit && period?._id && (
            <Button
              type="primary"
              icon={<RocketOutlined />}
              loading={deploying}
              onClick={handleSaveAndDeploy}
            >
              Deploy Contract
            </Button>
          )}
          {isEditMode && !hasContract && edit && (
            <Button
              type="primary"
              icon={<RocketOutlined />}
              loading={deploying}
              onClick={handleSaveAndDeploy}
            >
              Save & Deploy Contract
            </Button>
          )}
          {edit && !hasContract && (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={handleDelete} />
          )}
        </div>
      </div>

      <Row gutter={12}>
        {/* Owner Address */}
        <Col span={12}>
          <Form.Item
            {...restField}
            label="Owner Address"
            name={[name, 'ownerAddress']}
            rules={[{ required: true, message: 'Please input owner address!' }]}
          >
            <Input
              disabled={locked}
              placeholder="Enter owner address"
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
            />
          </Form.Item>
        </Col>

        {/* Start Date */}
        <Col span={12}>
          <Form.Item
            {...restField}
            label="Start Date"
            name={[name, 'startDate']}
            rules={[
              { required: true, message: 'Please select start date!' },
              {
                validator: (_, value) => {
                  const prevPeriodEndDate = form.getFieldValue([
                    'rentalPeriods',
                    name - 1,
                    'endDate',
                  ])

                  if (value && prevPeriodEndDate && value < prevPeriodEndDate) {
                    return Promise.reject(
                      'Start date must be greater than previous Period end date!'
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
            getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
            getValueProps={(value) => ({
              value: value ? convertNumberToDateTime(value) : null,
            })}
          >
            <DatePicker className="w-full" disabled={locked} />
          </Form.Item>
        </Col>

        {/* End Date */}
        <Col span={12}>
          <Form.Item
            {...restField}
            label="End Date"
            name={[name, 'endDate']}
            dependencies={[['rentalPeriods', name, 'startDate']]}
            rules={[
              { required: true, message: 'Please select end date!' },
              {
                validator: (_, value) => {
                  const startDate = form.getFieldValue(['rentalPeriods', name, 'startDate'])
                  if (value && startDate && value <= startDate) {
                    return Promise.reject('End date must be greater than start date!')
                  }
                  return Promise.resolve()
                },
              },
            ]}
            getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
            getValueProps={(value) => ({
              value: value ? convertNumberToDateTime(value) : null,
            })}
          >
            <DatePicker className="w-full" disabled={locked} />
          </Form.Item>
        </Col>

        {/* Frequency Date */}
        <Col span={12}>
          <Form.Item
            {...restField}
            label="Frequency Date"
            name={[name, 'frequencyDate']}
            rules={[{ required: true, message: 'Please select frequency!' }]}
          >
            <Select
              disabled={locked}
              placeholder="Select frequency"
              options={RENTAL_FREQUENCY_OPTIONS}
            />
          </Form.Item>
        </Col>

        {/* Rewards per NFT */}
        <Col span={12}>
          <Row gutter={8} align="bottom">
            <Col span={18}>
              <Form.Item
                {...restField}
                label="Rewards per NFT"
                name={[name, 'rewardsPerNFT']}
                rules={[
                  { required: true, message: 'Please input rewards per NFT!' },
                  {
                    type: 'number',
                    min: 0.01,
                    message: 'Rewards must be greater than or equal to 0.01!',
                  },
                ]}
              >
                <InputNumber
                  disabled={locked}
                  placeholder="Enter rewards per NFT"
                  style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                  min={0.01}
                />
              </Form.Item>
            </Col>

            {/* Token Symbol */}
            <Col span={6}>
              <Form.Item
                {...restField}
                name={[name, 'tokenSymbol']}
                rules={[{ required: true, message: 'Please select token!' }]}
              >
                <Select
                  disabled={locked || !!firstTokenSymbol}
                  placeholder="Select token"
                  options={RENTAL_TOKEN_OPTIONS}
                  onChange={(value) => {
                    const settingKey = TOKEN_ADDRESS_MAP[value]
                    const tokenAddress = settingKey ? getSetting(settingKey) : null
                    if (tokenAddress) {
                      form.setFieldValue(['rentalPeriods', name, 'tokenReward'], tokenAddress)
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* Annual Net Cashflow to Investor */}
        <Col span={12}>
          <Form.Item
            {...restField}
            label="Annual Net Cashflow to Investor"
            name={[name, 'annualNetCashflowToInvestor']}
            rules={[
              { required: true, message: 'Please input annual net cashflow!' },
              {
                type: 'number',
                min: 0.01,
                message: 'Cashflow must be greater than or equal to 0.01!',
              },
            ]}
          >
            <InputNumber
              disabled={!edit}
              placeholder="Enter annual net cashflow"
              style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
              min={0.01}
              addonBefore="$"
            />
          </Form.Item>
        </Col>

        {/* Token Reward (hidden) */}
        <Form.Item {...restField} name={[name, 'tokenReward']} hidden>
          <Input />
        </Form.Item>

        {/* Staking NFT Address (edit mode only) */}
        {isEditMode && (
          <Col span={12}>
            <Form.Item
              {...restField}
              label="Staking NFT Address"
              name={[name, 'nftStakePool']}
              rules={[{ required: true, message: 'Please select staking NFT!' }]}
            >
              <Select
                disabled={locked}
                placeholder="Select staking NFT"
                options={stakingNFTOptions}
              />
            </Form.Item>
          </Col>
        )}

        {/* Contract Address (edit mode only) */}
        {isEditMode && (
          <Col span={12}>
            <Form.Item {...restField} label="Contract Address" name={[name, 'contractAddress']}>
              <Input
                className="rental-period__copy-icon"
                disabled
                placeholder="Not deployed"
                style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                suffix={
                  form.getFieldValue(['rentalPeriods', name, 'contractAddress']) ? (
                    <CopyOutlined
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                      onClick={() => {
                        copy(form.getFieldValue(['rentalPeriods', name, 'contractAddress']))
                        message.success('Copied Success!')
                      }}
                    />
                  ) : null
                }
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  )
}

export default RentalPeriodCard
